import { TeamRedirectHandler } from '@features/organisation/components/index'
import { TeamListGroupsRedirect } from '@features/organisation/components/teamList/TeamListGroupsRedirect'
import { TeamMemberEditScreenRedirect } from '@features/organisation/components/teamMemberEdit/index'
import { TeamMemberProfileScreenRedirect } from '@features/organisation/components/teamMemberProfile/TeamMemberProfileRedirect'
import {
  CarerSkillsScreen,
  DeviceAccessScreen,
  DeviceManageScreen,
  DeviceSignsScreen,
  OrganisationDocumentsScreen,
  ShiftPasswordsScreen,
  TeamScreen
} from '@features/organisation/index'
import { OrganisationScreenFeatureFlagWrapper } from '@features/organisation/screens/OrganisationScreenFeatureFlagWrapper'
import { UserRole } from '@lmc/ts-common'
import type { ReactElement } from 'react'
import { Navigate, Route } from 'react-router-dom'
import {
  ContractAndPreferencesScreen,
  EditContractAndPreferencesScreen
} from 'screens/Rostering/screens'
import AllAbsencesScreen from 'screens/Rostering/teamMembers/Absences/AllAbsences'
import EditIndividualAbsenceScreen from 'screens/Rostering/teamMembers/Absences/EditIndividualAbsence'
import IndividualAbsenceScreen from 'screens/Rostering/teamMembers/Absences/IndividualAbsence'
import { ID, PROFILE } from './utils'

const ORGANISATION_PATH = 'organisation'
const TEAM = 'team'
const LIST = 'list'
const SHIFT_PASSWORDS = 'shift-passwords'
const DOCUMENTS = 'documents'
const DEVICES = 'devices'
const ALL_ABSENCES = 'all-absences'
export const ORGANISATION = {
  INDEX: ORGANISATION_PATH,
  TEAM: `${ORGANISATION_PATH}/${TEAM}`,
  TEAM_PROFILE: `${ORGANISATION_PATH}/${TEAM}/${ID}/${PROFILE}`,
  TEAMS_LIST: `${ORGANISATION_PATH}/${TEAM}/${LIST}`
}

export const organizationRoutes = (role?: UserRole | null) => {
  // CM-3757: rostering (incl. absences) is not part of the IT & Security remit.
  // Hiding the nav subtab isn't enough — guard the absences routes so they
  // can't be reached via deep link by IT (mirrors the noticeboard route guard).
  const guardAndRedirectToRoot = (element: ReactElement): ReactElement =>
    role === UserRole.it ? <Navigate to="/" replace /> : element

  return (
    <Route
      path={ORGANISATION_PATH}
      element={<OrganisationScreenFeatureFlagWrapper />}
    >
      <Route path={LIST} element={<TeamListGroupsRedirect />} />
      <Route path={TEAM} element={<TeamScreen />}>
        <Route index element={<TeamRedirectHandler />} />
        <Route
          path={`${ID}/${PROFILE}`}
          element={<TeamMemberProfileScreenRedirect />}
        />
        <Route
          path={`:id/${PROFILE}/edit`}
          element={<TeamMemberEditScreenRedirect />}
        />
        <Route path=":id/skills" element={<CarerSkillsScreen />} />
        <Route
          path=":id/contract-and-preferences"
          element={<ContractAndPreferencesScreen />}
        />
        <Route
          path=":id/contract-and-preferences/edit"
          element={<EditContractAndPreferencesScreen />}
        />
        <Route
          path=":id/absences"
          element={guardAndRedirectToRoot(<AllAbsencesScreen />)}
        />
        <Route
          path=":id/absences/:absenceId"
          element={guardAndRedirectToRoot(<IndividualAbsenceScreen />)}
        />
        <Route
          path=":id/absences/:absenceId/edit"
          element={guardAndRedirectToRoot(<EditIndividualAbsenceScreen />)}
        />
      </Route>
      <Route path={SHIFT_PASSWORDS} element={<ShiftPasswordsScreen />} />
      <Route path={DOCUMENTS} element={<OrganisationDocumentsScreen />} />
      <Route path={DEVICES} element={<DeviceAccessScreen />} />
      <Route path="devices/:id/manage" element={<DeviceManageScreen />} />
      <Route path="devices/signs" element={<DeviceSignsScreen />} />
      <Route
        path={ALL_ABSENCES}
        element={guardAndRedirectToRoot(<AllAbsencesScreen all />)}
      />
      <Route
        path={`${ALL_ABSENCES}/:absenceId`}
        element={guardAndRedirectToRoot(<IndividualAbsenceScreen />)}
      />
      <Route
        path={`${ALL_ABSENCES}/:absenceId/edit`}
        element={guardAndRedirectToRoot(<EditIndividualAbsenceScreen />)}
      />
    </Route>
  )
}
